import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Admin-only view of every lock override logged by
 * utils/admin_override.log_admin_override — i.e. each time an admin
 * edited or deleted a record that was normally locked (approved invoice,
 * approved PO, invoiced delivery note, …).
 */
const ACTION_TONE = {
  update: 'bg-amber-100 text-amber-800',
  delete: 'bg-rose-100 text-rose-800',
};

const KIND_LABEL = {
  sales_invoice: 'Sales Invoice',
  purchase_order: 'Purchase Order',
  delivery_note: 'Delivery Note',
};

const AdminOverrideLog = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    record_kind: '',
    record_id: '',
    user_id: '',
    action: '',
  });

  const isAdmin = user && ['admin', 'super_admin'].includes(user.role);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const res = await api.get('/admin-override-log', { params });
      setItems(res.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="p-6 text-rose-700 font-semibold" data-testid="admin-override-log-denied">
        Admin access required.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="admin-override-log-page">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            🔐 Admin Override Audit Log
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Every time an admin bypassed an approval / invoiced lock to edit or delete a record. Auto-recorded — read-only.
          </p>
        </div>
        <button
          onClick={fetchData}
          className="px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 text-sm"
          data-testid="admin-override-log-refresh"
        >
          ↻ Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 bg-slate-50 rounded-md p-3 border">
        <select
          className="border rounded-md px-2 py-1.5 text-sm bg-white"
          value={filters.record_kind}
          onChange={(e) => setFilters({ ...filters, record_kind: e.target.value })}
          data-testid="admin-override-filter-kind"
        >
          <option value="">All record kinds</option>
          <option value="sales_invoice">Sales Invoice</option>
          <option value="purchase_order">Purchase Order</option>
          <option value="delivery_note">Delivery Note</option>
        </select>
        <select
          className="border rounded-md px-2 py-1.5 text-sm bg-white"
          value={filters.action}
          onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          data-testid="admin-override-filter-action"
        >
          <option value="">All actions</option>
          <option value="update">Update</option>
          <option value="delete">Delete</option>
        </select>
        <input
          type="text"
          placeholder="Record ID / Invoice #"
          className="border rounded-md px-2 py-1.5 text-sm"
          value={filters.record_id}
          onChange={(e) => setFilters({ ...filters, record_id: e.target.value })}
          data-testid="admin-override-filter-record-id"
        />
        <input
          type="text"
          placeholder="User ID"
          className="border rounded-md px-2 py-1.5 text-sm"
          value={filters.user_id}
          onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
          data-testid="admin-override-filter-user-id"
        />
        <button
          onClick={fetchData}
          className="bg-indigo-600 text-white rounded-md text-sm px-3 py-1.5 hover:bg-indigo-700"
          data-testid="admin-override-filter-apply"
        >
          Apply
        </button>
      </div>

      {error && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-md text-sm text-rose-800">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-override-log-table">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="px-3 py-2 text-left">When</th>
              <th className="px-3 py-2 text-left">User</th>
              <th className="px-3 py-2 text-left">Record</th>
              <th className="px-3 py-2 text-left">Lock Bypassed</th>
              <th className="px-3 py-2 text-left">Action</th>
              <th className="px-3 py-2 text-left">Before → After</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                  No admin overrides recorded.
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr key={row.id} className="border-t align-top hover:bg-slate-50">
                  <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                    {row.timestamp ? new Date(row.timestamp).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{row.user_name || row.user_email}</div>
                    <div className="text-xs text-slate-500">{row.user_role}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-mono text-slate-900">{row.record_id}</div>
                    <div className="text-xs text-slate-500">{KIND_LABEL[row.record_kind] || row.record_kind}</div>
                  </td>
                  <td className="px-3 py-2 text-amber-800 font-mono text-xs">{row.lock_reason}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${ACTION_TONE[row.action] || 'bg-slate-100 text-slate-800'}`}>
                      {row.action}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-700 max-w-md break-words">
                    {row.before && (
                      <div className="text-slate-500">
                        <span className="font-sans font-semibold">before:</span> {JSON.stringify(row.before)}
                      </div>
                    )}
                    {row.after && (
                      <div>
                        <span className="font-sans font-semibold">after:</span> {JSON.stringify(row.after)}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminOverrideLog;
